import { useEffect, useMemo, useState } from 'react'
import {
  FaBars,
  FaBell,
  FaBox,
  FaCartShopping,
  FaCircleCheck,
  FaClock,
  FaCreditCard,
  FaCloudArrowUp,
  FaEnvelope,
  FaGavel,
  FaGear,
  FaHeart,
  FaFilter,
  FaHouse,
  FaMagnifyingGlass,
  FaPlus,
  FaRightFromBracket,
  FaShieldHalved,
  FaTruckFast,
  FaTriangleExclamation,
  FaUser,
  FaUserGear,
  FaXmark,
} from 'react-icons/fa6'
import logo from '../images/logo.png'
import background from '../images/background.png'
import apiClient from '../services/apiClient'
import { resolveAssetUrl } from '../services/apiClient'
import { clearSession, getToken } from '../services/authStorage'
import { clearPaymentPromptPending, isPaymentPromptPending } from '../services/paymentOnboardingStorage'
import { confirmStripePaymentRecovery, hasStripePaymentRecovery } from '../services/stripeRecovery'
import { ROUTE_PATHS, dashboardRoute, navigateTo } from '../app/routes'
import OrdersPanel from '../features/dashboard/OrdersPanel'
import PaymentsPanel from '../features/dashboard/PaymentsPanel'
import VaultPanel from '../features/dashboard/VaultPanel'
import {
  buildVaultItems,
  buildVaultSummary,
  formatDashboardMoney as formatMoney,
  formatDateLabel,
} from '../features/dashboard/vaultUtils'
import './Dashboard.css'

const navItems = [
  { id: 'overview', label: 'Overview', icon: <FaHouse /> },
  { id: 'auctions', label: 'Auctions', icon: <FaGavel /> },
  { id: 'shop', label: 'Buy Now', icon: <FaCartShopping /> },
  { id: 'vault', label: 'The Vault', icon: <FaShieldHalved /> },
  { id: 'bundle', label: 'Weekly Bundle', icon: <FaBox /> },
  { id: 'orders', label: 'Orders', icon: <FaTruckFast /> },
  { id: 'consignments', label: 'Consignments', icon: <FaUserGear /> },
  { id: 'payments', label: 'Payments', icon: <FaCreditCard /> },
  { id: 'profile', label: 'Profile & Shipping', icon: <FaUser /> },
  { id: 'alerts', label: 'Notifications', icon: <FaBell /> },
  { id: 'settings', label: 'Settings', icon: <FaGear /> },
]

const sectionDescriptions = {
  overview: 'Operational snapshot for bidding, buying, vault, weekly bundle, orders, and account readiness.',
  auctions: 'Timed auctions, anti-sniping status, hard-close lots, live-style bids, bid history, and watchlist actions.',
  shop: 'Fixed-price products, memorabilia, quick checkout, and add-to-weekly-bundle actions.',
  vault: 'Items won, purchased, watched, held, and profile records tied to the collector account.',
  bundle: 'Weekly hold, Friday combined checkout, manual checkout, shipping estimate, and unpaid item reminders.',
  orders: 'Order history, won items, purchases, payment status, and shipping progress.',
  consignments: 'Submit cards and memorabilia for OBC review, professional listing, sale tracking, and manual payout.',
  payments: 'Saved payment methods, verified bidder card, quick checkout readiness, and payment reminders.',
  profile: 'Shipping details, profile information, notification preferences, and account verification.',
  alerts: 'Outbid alerts, ending alerts, winning notices, checkout reminders, new drops, and announcements.',
  settings: 'Theme preference, account security, bidder status, and notification controls.',
}

function dashboardImage(asset) {
  return asset?.url ? resolveAssetUrl(asset.url) : ''
}

function getAccountInitials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'OC'
}

function DashboardImage({ alt, src }) {
  if (!src) {
    return <span className="obc-dashboard__image-placeholder" aria-label={`${alt} has no uploaded image`}>No image</span>
  }

  return <img src={src} alt={alt} />
}

function formatAuctionDashboardStatus(auction, accountId) {
  if (auction.status === 'won' && auction.highBidderId === accountId) return 'Won'
  if (auction.status === 'live' && auction.highBidderId === accountId) return 'Leading'
  if (auction.status === 'scheduled') return 'Scheduled'
  if (auction.status === 'closed') return 'Closed'
  if (auction.status === 'won') return 'Won'
  return 'Live'
}

function getEffectiveAuctionStatus(lot, now = Date.now()) {
  const status = lot?.rawStatus || lot?.status
  const startsAt = lot?.startsAt ? new Date(lot.startsAt).getTime() : NaN
  const endsAt = lot?.endsAt ? new Date(lot.endsAt).getTime() : NaN

  if (status === 'scheduled' && Number.isFinite(startsAt) && startsAt <= now) {
    return Number.isFinite(endsAt) && endsAt <= now ? 'closed' : 'live'
  }

  if (status === 'live' && Number.isFinite(endsAt) && endsAt <= now) {
    return 'closed'
  }

  return status || 'closed'
}

function dashboardAuctionCanBid(lot, now = Date.now()) {
  const endsAt = lot?.endsAt ? new Date(lot.endsAt).getTime() : NaN

  return getEffectiveAuctionStatus(lot, now) === 'live'
    && Number.isFinite(endsAt)
    && endsAt > now
    && Number(lot?.nextBidAmount || 0) > 0
}

function formatEffectiveDashboardStatus(lot, now = Date.now()) {
  const status = getEffectiveAuctionStatus(lot, now)

  if (status === 'live' && lot?.status === 'Leading') return 'Leading'
  if (status === 'live') return 'Live'
  if (status === 'scheduled') return 'Scheduled'
  if (status === 'won') return 'Won'
  if (status === 'closed' && lot?.rawStatus === 'live') return 'Finalizing'

  return 'Closed'
}

function formatDashboardCountdown(targetDate, now) {
  if (!targetDate) return 'Pending schedule'

  const target = new Date(targetDate).getTime()

  if (!Number.isFinite(target)) return 'Pending schedule'

  const remaining = Math.max(0, target - now)
  const days = Math.floor(remaining / 86400000)
  const hours = Math.floor((remaining % 86400000) / 3600000)
  const minutes = Math.floor((remaining % 3600000) / 60000)
  const seconds = Math.floor((remaining % 60000) / 1000)

  if (days > 0) return `${days}d ${hours}h ${minutes}m`

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function getDashboardAuctionClock(lot, now) {
  if (!lot) return { label: 'Auction clock', value: 'Pending schedule', tone: 'neutral' }
  const effectiveStatus = getEffectiveAuctionStatus(lot, now)

  if (effectiveStatus === 'scheduled') {
    return {
      label: 'Starts in',
      value: formatDashboardCountdown(lot.startsAt, now),
      tone: 'scheduled',
    }
  }

  if (effectiveStatus === 'live') {
    const hasClosed = lot.endsAt && new Date(lot.endsAt).getTime() <= now

    return {
      label: hasClosed ? 'Finalizing' : 'Closes in',
      value: hasClosed ? '00:00:00' : formatDashboardCountdown(lot.endsAt, now),
      tone: hasClosed ? 'danger' : 'live',
    }
  }

  return {
    label: lot.rawStatus === 'live' ? 'Finalizing' : ['Won', 'Closed'].includes(lot.status) ? 'Closed' : 'Auction clock',
    value: lot.endsLabel || lot.timer || 'Closed',
    tone: 'closed',
  }
}

function getDashboardCountdownSegments(lot, now) {
  const targetDate = getEffectiveAuctionStatus(lot, now) === 'scheduled' ? lot?.startsAt : lot?.endsAt
  const target = targetDate ? new Date(targetDate).getTime() : NaN
  const remaining = Number.isFinite(target) ? Math.max(0, target - now) : 0
  const days = Math.floor(remaining / 86400000)
  const hours = Math.floor((remaining % 86400000) / 3600000)
  const minutes = Math.floor((remaining % 3600000) / 60000)
  const seconds = Math.floor((remaining % 60000) / 1000)

  return [
    { label: 'Days', value: String(days).padStart(2, '0') },
    { label: 'Hours', value: String(hours).padStart(2, '0') },
    { label: 'Minutes', value: String(minutes).padStart(2, '0') },
    { label: 'Seconds', value: String(seconds).padStart(2, '0') },
  ]
}

function formatDashboardBidTime(value) {
  if (!value) return 'Recently'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return 'Recently'

  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function resolveUserOrderStage(order) {
  if (order.status === 'paid') {
    return { label: 'Paid', tone: 'success', action: 'Shipment pending', payable: false }
  }

  if (order.status === 'shipped') {
    return { label: 'Shipped', tone: 'success', action: 'Track shipment', payable: false }
  }

  if (order.status === 'relist-queued') {
    return { label: 'Relist queued', tone: 'danger', action: 'Contact OBC', payable: false }
  }

  if (order.status === 'penalty-applied') {
    return { label: 'Penalty applied', tone: 'danger', action: 'Pay before relist', payable: true }
  }

  if (order.status === 'payment-pending') {
    return { label: 'Payment due', tone: 'warning', action: 'Pay now', payable: true }
  }

  if (order.status === 'weekly-hold') {
    return { label: 'Weekly hold', tone: 'neutral', action: 'Bundle checkout', payable: false }
  }

  return { label: order.status?.replace(/-/g, ' ') || 'Monitor', tone: 'neutral', action: 'Monitor', payable: false }
}

function UserDashboard({ section = 'overview' }) {
  const activeSection = section
  const [apiToken, setApiToken] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [bundleData, setBundleData] = useState(null)
  const [notificationData, setNotificationData] = useState(null)
  const [apiStatus, setApiStatus] = useState('Connecting to OBC API...')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [paymentPromptPending, setPaymentPromptPending] = useState(isPaymentPromptPending)
  const activeLabel = navItems.find((item) => item.id === activeSection)?.label || 'Overview'
  const isDashboardLoading = !dashboardData && apiStatus.startsWith('Connecting')
  const shouldShowStatusToast = Boolean(apiStatus) && !apiStatus.startsWith('Connecting')
  const statusTone = /failed|could not|expired|required|error/i.test(apiStatus) ? 'danger' : 'success'
  const shouldShowPaymentPrompt = Boolean(
    paymentPromptPending
    && dashboardData?.account
    && dashboardData.account.role !== 'admin'
    && !dashboardData.account.savedPaymentMethods?.length
    && activeSection !== 'payments'
  )
  const navigateSection = (nextSection) => {
    navigateTo(dashboardRoute(ROUTE_PATHS.dashboard, nextSection))
  }

  useEffect(() => {
    if (!shouldShowStatusToast) return undefined

    const timeout = window.setTimeout(() => {
      setApiStatus('')
    }, statusTone === 'danger' ? 6500 : 4200)

    return () => window.clearTimeout(timeout)
  }, [apiStatus, shouldShowStatusToast, statusTone])

  const closePaymentPrompt = () => {
    clearPaymentPromptPending()
    setPaymentPromptPending(false)
  }

  const openPaymentSetup = () => {
    clearPaymentPromptPending()
    setPaymentPromptPending(false)
    navigateSection('payments')
  }

  useEffect(() => {
    let isMounted = true

    async function loadDashboard() {
      const token = getToken()

      if (!token) {
        navigateTo('/login')
        return
      }

      try {
        const [dashboard, bundle, notifications] = await Promise.all([
          apiClient.userDashboard(token),
          apiClient.weeklyBundle(token),
          apiClient.notifications(token, { limit: 50 }),
        ])

        if (isMounted) {
          setApiToken(token)
          setDashboardData(dashboard)
          setBundleData(bundle)
          setNotificationData(notifications)
          setApiStatus('Server connected')
        }
      } catch (error) {
        clearSession()
        if (isMounted) {
          setApiStatus(error.message || 'Session expired')
          navigateTo('/login')
        }
      }
    }

    loadDashboard()

    return () => {
      isMounted = false
    }
  }, [])

  const liveSummaryCards = useMemo(() => {
    if (!dashboardData) return []

    return [
      {
        label: 'Verified bidder',
        value: dashboardData.account.verifiedBidder ? 'Active' : 'Required',
        note: 'Card required before bidding',
        icon: <FaCircleCheck />,
        tone: dashboardData.account.verifiedBidder ? 'success' : 'danger',
      },
      { label: 'Active bids', value: String(dashboardData.summary.activeAuctions), note: 'Live auction lane', icon: <FaGavel />, tone: 'warning' },
      { label: 'Weekly hold', value: `${dashboardData.summary.weeklyBundleItems} items`, note: 'Friday combined checkout', icon: <FaBox />, tone: 'success' },
      { label: 'Unpaid wins', value: String(dashboardData.summary.unpaidWins), note: 'Penalty and relist protection', icon: <FaTriangleExclamation />, tone: dashboardData.summary.unpaidWins ? 'danger' : 'success' },
    ]
  }, [dashboardData])

  const liveAuctions = useMemo(() => {
    if (!dashboardData) return []
    const recentBidByAuction = new Map()
    const recentBids = dashboardData.recentBids || []

    recentBids.forEach((bid) => {
      if (!recentBidByAuction.has(bid.auctionId)) {
        recentBidByAuction.set(bid.auctionId, bid)
      }
    })

    return dashboardData.auctions.map((auction) => {
      const latestUserBid = recentBidByAuction.get(auction.id)

      return {
        id: auction.id,
        name: auction.title,
        detail: auction.subtitle,
        rawStatus: auction.status,
        status: formatAuctionDashboardStatus(auction, dashboardData.account.id),
        isWatched: auction.watchlistedBy?.includes(dashboardData.account.id),
        type: auction.type,
        category: auction.category || 'Cards',
        bid: formatMoney(auction.currentBid),
        currentBidAmount: Number(auction.currentBid || 0),
        nextBid: formatMoney(auction.nextBid),
        nextBidAmount: Number(auction.nextBid || 0),
        minimumIncrement: Number(auction.minimumIncrement || 1),
        reserve: formatMoney(auction.reservePrice || 0),
        reserveMet: Boolean(auction.reserveMet || Number(auction.currentBid || 0) >= Number(auction.reservePrice || 0) || !Number(auction.reservePrice || 0)),
        bids: String(auction.bidCount),
        bidCount: Number(auction.bidCount || 0),
        startsAt: auction.startsAt,
        endsAt: auction.endsAt,
        startsLabel: formatDateLabel(auction.startsAt),
        endsLabel: formatDateLabel(auction.endsAt),
        timer: auction.status === 'scheduled' ? formatDateLabel(auction.startsAt) : formatDateLabel(auction.endsAt),
        userLatestBid: latestUserBid ? {
          amount: formatMoney(latestUserBid.amount),
          amountValue: Number(latestUserBid.amount || 0),
          at: formatDashboardBidTime(latestUserBid.createdAt),
          extendedClose: Boolean(latestUserBid.extendedClose),
        } : null,
        image: dashboardImage(auction.images?.[0]),
      }
    })
  }, [dashboardData])

  const liveBuyNowItems = useMemo(() => {
    if (!dashboardData) return []
    const activeListingOrders = new Set(
      dashboardData.orders
        .filter((order) => order.listingId && ['weekly-hold', 'payment-pending', 'paid'].includes(order.status))
        .map((order) => order.listingId)
    )

    return dashboardData.buyNowListings.map((item, index) => ({
      id: item.id,
      name: item.title,
      detail: item.category,
      category: item.category,
      price: formatMoney(item.price),
      inventory: item.inventory,
      heldByUser: activeListingOrders.has(item.id),
      bundleEligible: item.bundleEligible,
      available: item.status === 'published' && item.inventory > 0,
      image: dashboardImage(item.images?.[0]),
    }))
  }, [dashboardData])

  const liveOrders = useMemo(() => {
    if (!dashboardData) return []

    return dashboardData.orders.map((order) => {
      const stage = resolveUserOrderStage(order)

      return {
        rawId: order.id,
        rawStatus: order.status,
        rawDueAt: order.paymentDueAt || null,
        rawPenaltyAt: order.penaltyAt || null,
        rawRelistAt: order.relistEligibleAt || null,
        id: order.id.replace('ord_', 'OBC-'),
        item: order.itemTitle,
        status: order.status.replace(/-/g, ' '),
        stage: stage.label,
        stageTone: stage.tone,
        action: stage.action,
        payable: stage.payable,
        total: formatMoney(order.total),
        shipping: order.shippingStatus?.replace(/-/g, ' ') || 'not ready',
        tracking: order.trackingNumber || 'Pending',
        reminders: order.reminderCount || 0,
        due: order.paymentDueAt ? new Date(order.paymentDueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No due date',
        penaltyAt: order.penaltyAt ? new Date(order.penaltyAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not scheduled',
        relistAt: order.relistEligibleAt ? new Date(order.relistEligibleAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not eligible',
      }
    })
  }, [dashboardData])

  const liveBundleRows = useMemo(() => {
    if (!dashboardData) return []

    return dashboardData.orders
      .filter((order) => order.status === 'weekly-hold' || order.status === 'payment-pending')
      .map((order) => ({
        id: order.id,
        item: order.itemTitle,
        price: formatMoney(order.total),
        status: order.status.replace(/-/g, ' '),
        shipping: order.shippingStatus?.replace(/-/g, ' ') || 'not ready',
        due: order.paymentDueAt ? new Date(order.paymentDueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Friday bundle',
        hold: order.weeklyHold ? 'Weekly hold' : 'Manual checkout',
      }))
  }, [dashboardData])

  const liveNotifications = useMemo(() => {
    const sourceNotifications = notificationData?.notifications || dashboardData?.notifications

    if (!sourceNotifications) return []

    return sourceNotifications.map((notice) => ({
      id: notice.id,
      rawType: notice.type,
      type: notice.type.replace(/-/g, ' '),
      message: notice.message,
      metadata: notice.metadata || {},
      actionPath: notice.metadata?.actionPath || null,
      read: Boolean(notice.read),
      time: new Date(notice.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
    }))
  }, [dashboardData, notificationData])

  const unreadNotificationCount = Number.isFinite(notificationData?.unreadCount)
    ? notificationData.unreadCount
    : liveNotifications.filter((notice) => !notice.read).length

  const liveConsignments = useMemo(() => {
    if (!dashboardData?.consignments) return []

    return dashboardData.consignments.map((item) => ({
      id: item.id,
      item: item.itemTitle,
      estimate: formatMoney(item.estimate || 0),
      saleAmount: formatMoney(item.saleAmount || 0),
      payoutAmount: formatMoney(item.payoutAmount || 0),
      expectedPayoutAmount: formatMoney(item.expectedPayoutAmount || 0),
      stage: item.workflowStage || 'Monitor',
      stageTone: item.workflowTone || 'neutral',
      status: item.status?.replace(/-/g, ' ') || 'submitted',
      sale: item.saleStatus?.replace(/-/g, ' ') || 'pending review',
      payout: item.payoutStatus?.replace(/-/g, ' ') || 'not started',
      nextAction: item.nextAction || 'OBC review',
      source: item.sourceLabel || 'Not listed',
      timeline: item.timeline || [],
      paidAt: item.payoutPaidAt ? new Date(item.payoutPaidAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not paid',
      image: dashboardImage(item.images?.[0]),
    }))
  }, [dashboardData])

  const liveAccount = dashboardData?.account || null
  const accountName = liveAccount?.name || 'Collector'
  const accountEmail = liveAccount?.email || 'Signed in account'
  const accountInitials = getAccountInitials(accountName)
  const accountStatusLabel = liveAccount?.verifiedBidder ? 'Verified bidder' : isDashboardLoading ? 'Loading account' : 'Verification needed'
  const accountStatusTone = liveAccount?.verifiedBidder ? 'success' : isDashboardLoading ? 'neutral' : 'warning'

  const liveVaultItems = useMemo(() => {
    return buildVaultItems(dashboardData, dashboardImage)
  }, [dashboardData])

  const liveVaultSummary = useMemo(() => {
    return buildVaultSummary({ bundleData, dashboardData, totalItems: liveVaultItems.length })
  }, [bundleData, dashboardData, liveVaultItems.length])

  const visibleAuctions = useMemo(
    () => {
      if (activeSection === 'watchlist') return liveAuctions.filter((lot) => lot.isWatched)
      return liveAuctions
    },
    [activeSection, liveAuctions]
  )

  const handleBid = async (auction, bidAmount = auction?.nextBidAmount) => {
    if (!apiToken || !auction?.id || !bidAmount) return false

    try {
      const result = await apiClient.placeBid(apiToken, auction.id, Number(bidAmount))
      const nextDashboard = await apiClient.userDashboard(apiToken)
      setDashboardData(nextDashboard)
      setApiStatus(result.bid?.extendedClose ? `Bid placed on ${auction.name}. Anti-snipe timer extended.` : `Bid placed on ${auction.name}`)
      return true
    } catch (error) {
      setApiStatus(error.message || 'Bid failed')
      return false
    }
  }

  const handleLoadAuctionBids = async (auction) => {
    if (!apiToken || !auction?.id) return { bids: [] }

    return apiClient.auctionBids(apiToken, auction.id)
  }

  const handleToggleWatchlist = async (auction) => {
    if (!apiToken || !auction.id) return

    try {
      if (auction.isWatched) {
        await apiClient.removeFromWatchlist(apiToken, auction.id)
        await refreshDashboard(`${auction.name} removed from watchlist.`)
      } else {
        await apiClient.addToWatchlist(apiToken, auction.id)
        await refreshDashboard(`${auction.name} added to watchlist.`)
      }
    } catch (error) {
      setApiStatus(error.message || 'Watchlist update failed')
    }
  }

  const refreshDashboard = async (message) => {
    if (!apiToken) return
    const [nextDashboard, nextBundle, nextNotifications] = await Promise.all([
      apiClient.userDashboard(apiToken),
      apiClient.weeklyBundle(apiToken),
      apiClient.notifications(apiToken, { limit: 50 }),
    ])
    setDashboardData(nextDashboard)
    setBundleData(nextBundle)
    setNotificationData(nextNotifications)
    setApiStatus(message)
  }

  const refreshNotifications = async (message) => {
    if (!apiToken) return
    const nextNotifications = await apiClient.notifications(apiToken, { limit: 50 })
    setNotificationData(nextNotifications)
    setApiStatus(message)
  }

  const handleBundleCheckout = async () => {
    if (!apiToken) return

    try {
      const result = await apiClient.checkoutWeeklyBundle(apiToken)
      await refreshDashboard(`Checkout complete: ${formatMoney(result.payment.amount)} paid`)
    } catch (error) {
      if (hasStripePaymentRecovery(error)) {
        try {
          setApiStatus('Stripe needs card authentication. Opening secure confirmation...')
          await confirmStripePaymentRecovery(error)
          await refreshDashboard('Stripe authentication complete. Payment confirmation is being reconciled.')
          return
        } catch (recoveryError) {
          setApiStatus(recoveryError.message || 'Stripe authentication failed')
          return
        }
      }

      setApiStatus(error.message || 'Checkout failed')
    }
  }

  const handleAddPaymentMethod = async (payload = { brand: 'Visa', last4: '4242', primary: true }) => {
    if (!apiToken) return

    try {
      await apiClient.addPaymentMethod(apiToken, payload)
      await refreshDashboard('Payment method saved. Bidder verification active.')
    } catch (error) {
      setApiStatus(error.message || 'Payment method failed')
    }
  }

  const handleCreatePaymentSetup = async () => {
    if (!apiToken) {
      throw new Error('Sign in before setting up a bidder card.')
    }

    setApiStatus('Starting secure Stripe card setup...')
    return apiClient.createPaymentSetupIntent(apiToken)
  }

  const handleCompletePaymentSetup = async (payload) => {
    if (!apiToken) return

    try {
      await apiClient.completePaymentSetup(apiToken, payload)
      await refreshDashboard('Stripe card saved. Bidder verification active.')
    } catch (error) {
      setApiStatus(error.message || 'Could not save Stripe card setup.')
      throw error
    }
  }

  const handleSetPrimaryPaymentMethod = async (methodId) => {
    if (!apiToken || !methodId) return

    try {
      await apiClient.setPrimaryPaymentMethod(apiToken, methodId)
      await refreshDashboard('Primary payment method updated.')
    } catch (error) {
      setApiStatus(error.message || 'Could not update primary payment method')
    }
  }

  const handleRemovePaymentMethod = async (methodId) => {
    if (!apiToken || !methodId) return

    try {
      await apiClient.removePaymentMethod(apiToken, methodId)
      await refreshDashboard('Payment method removed. Bidder verification depends on having at least one saved card.')
    } catch (error) {
      setApiStatus(error.message || 'Could not remove payment method')
    }
  }

  const handleAddBuyNowToBundle = async (item) => {
    if (!apiToken || !item.id) return

    try {
      await apiClient.addBuyNowToBundle(apiToken, item.id)
      await refreshDashboard(`${item.name} added to weekly bundle.`)
    } catch (error) {
      setApiStatus(error.message || 'Could not add item to bundle')
    }
  }

  const handleQuickCheckoutBuyNow = async (item) => {
    if (!apiToken || !item.id) return

    try {
      const result = await apiClient.quickCheckoutBuyNow(apiToken, item.id)
      await refreshDashboard(`Quick checkout complete: ${formatMoney(result.payment.amount)} paid.`)
    } catch (error) {
      if (hasStripePaymentRecovery(error)) {
        try {
          setApiStatus('Stripe needs card authentication. Opening secure confirmation...')
          await confirmStripePaymentRecovery(error)
          await refreshDashboard('Stripe authentication complete. Payment confirmation is being reconciled.')
          return
        } catch (recoveryError) {
          setApiStatus(recoveryError.message || 'Stripe authentication failed')
          return
        }
      }

      setApiStatus(error.message || 'Quick checkout failed')
    }
  }

  const handlePayOrder = async (order) => {
    if (!apiToken || !order?.rawId) return

    try {
      const result = await apiClient.payOrder(apiToken, order.rawId)
      await refreshDashboard(`Order checkout complete: ${formatMoney(result.payment.amount)} paid.`)
    } catch (error) {
      if (hasStripePaymentRecovery(error)) {
        try {
          setApiStatus('Stripe needs card authentication. Opening secure confirmation...')
          await confirmStripePaymentRecovery(error)
          await refreshDashboard('Stripe authentication complete. Payment confirmation is being reconciled.')
          return
        } catch (recoveryError) {
          setApiStatus(recoveryError.message || 'Stripe authentication failed')
          return
        }
      }

      setApiStatus(error.message || 'Order checkout failed')
    }
  }

  const handleCreateConsignment = async ({ file, payload }) => {
    if (!apiToken) return

    try {
      setApiStatus(file ? 'Uploading consignment image...' : 'Submitting consignment...')
      const images = payload?.images?.length
        ? payload.images
        : file
          ? [(await apiClient.uploadUserImage(apiToken, file)).asset]
          : []
      await apiClient.createConsignment(apiToken, { ...payload, images })
      await refreshDashboard('Consignment submitted for OBC review.')
    } catch (error) {
      setApiStatus(error.message || 'Consignment submission failed')
    }
  }

  const handleUpdateProfile = async (payload) => {
    if (!apiToken) return

    try {
      await apiClient.updateProfile(apiToken, payload)
      await refreshDashboard('Profile and shipping details saved.')
    } catch (error) {
      setApiStatus(error.message || 'Profile update failed')
    }
  }

  const handleUpdateNotificationPreferences = async (payload) => {
    if (!apiToken) return

    try {
      await apiClient.updateNotificationPreferences(apiToken, payload)
      await refreshDashboard('Notification preferences saved.')
    } catch (error) {
      setApiStatus(error.message || 'Notification preferences failed')
    }
  }

  const handleMarkNotificationRead = async (notice) => {
    if (!apiToken || !notice.id || notice.read) return

    try {
      await apiClient.markNotificationRead(apiToken, notice.id)
      await refreshNotifications('Notification marked read.')
    } catch (error) {
      setApiStatus(error.message || 'Notification update failed')
    }
  }

  const handleMarkAllNotificationsRead = async () => {
    if (!apiToken) return

    try {
      await apiClient.markAllNotificationsRead(apiToken)
      await refreshNotifications('All notifications marked read.')
    } catch (error) {
      setApiStatus(error.message || 'Notification update failed')
    }
  }

  const handleOpenNotification = (notice) => {
    if (notice?.actionPath) {
      navigateTo(notice.actionPath)
    }
  }

  return (
    <section className={`obc-dashboard obc-dashboard--functional ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''}`}>
      <aside className="obc-dashboard__sidebar">
        <button
          className="obc-dashboard__sidebar-toggle"
          type="button"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!sidebarCollapsed}
          onClick={() => setSidebarCollapsed((current) => !current)}
        >
          <FaBars />
        </button>

        <button className="obc-dashboard__logo" type="button" onClick={() => navigateSection('overview')}>
          <img className="obc-dashboard__logo-img" src={logo} alt="OBC Cards and Collectibles" />
        </button>

        <nav className="obc-dashboard__nav">
          {navItems.map((item) => (
            <button
              className={activeSection === item.id ? 'is-active' : undefined}
              type="button"
              onClick={() => navigateSection(item.id)}
              title={item.label}
              key={item.id}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="obc-dashboard__operator">
          <div className="obc-dashboard__avatar">
            <FaUser />
          </div>
          <div>
            <span>Collector account</span>
            <strong>{dashboardData?.account?.verifiedBidder ? 'Bidder verified' : isDashboardLoading ? 'Loading account' : 'Verification needed'}</strong>
          </div>
        </div>

        <button className="obc-dashboard__logout" type="button" title="Log Out" onClick={() => { clearSession(); navigateTo('/login') }}>
          <FaRightFromBracket />
          <span>Log Out</span>
        </button>
      </aside>

      <main className="obc-dashboard__main">
        <header className="obc-dashboard__topbar">
          <h2>{activeLabel}</h2>
          <label className="obc-dashboard__search">
            <FaMagnifyingGlass />
            <input placeholder="Search bids, orders, cards, lots..." type="text" />
          </label>
          <div className="obc-dashboard__top-actions">
            <div className="obc-dashboard__user obc-dashboard__user--identity">
              <div className="obc-dashboard__avatar obc-dashboard__avatar--small">
                <span>{accountInitials}</span>
              </div>
              <div className="obc-dashboard__user-copy">
                <span>{accountName}</span>
                <strong>{accountEmail}</strong>
              </div>
              <span className={`obc-dashboard__user-status is-${accountStatusTone}`}>{accountStatusLabel}</span>
            </div>
            <button type="button" aria-label="Notifications" onClick={() => navigateSection('alerts')}>
              <FaBell />
              {unreadNotificationCount ? <span>{unreadNotificationCount}</span> : null}
            </button>
            <button type="button" aria-label="Weekly bundle" onClick={() => navigateSection('bundle')}>
              <FaCartShopping />
            </button>
          </div>
        </header>

        <div className="obc-dashboard__canvas">
          {shouldShowStatusToast ? (
            <div className={`obc-dashboard__toast is-${statusTone}`} role={statusTone === 'danger' ? 'alert' : 'status'} aria-live={statusTone === 'danger' ? 'assertive' : 'polite'}>
              <span className="obc-dashboard__toast-icon">
                {statusTone === 'danger' ? <FaTriangleExclamation /> : <FaCircleCheck />}
              </span>
              <span className="obc-dashboard__toast-message">{apiStatus}</span>
              <button type="button" onClick={() => setApiStatus('')} aria-label="Dismiss notification">
                <FaXmark />
              </button>
            </div>
          ) : null}

          {shouldShowPaymentPrompt ? (
            <PaymentCardPrompt account={dashboardData.account} onClose={closePaymentPrompt} onSetup={openPaymentSetup} />
          ) : null}

          {/* <DashboardHeader activeLabel={activeLabel} activeSection={activeSection} onSectionChange={navigateSection} /> */}

          {isDashboardLoading ? (
            <DashboardSkeleton activeSection={activeSection} />
          ) : activeSection === 'overview' ? (
            <Overview
              auctions={liveAuctions}
              buyNowItems={liveBuyNowItems}
              bundleRows={liveBundleRows}
              bundleSummary={bundleData}
              notifications={liveNotifications}
              onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
              onMarkNotificationRead={handleMarkNotificationRead}
              onOpenNotification={handleOpenNotification}
              onPayOrder={handlePayOrder}
              onAddToBundle={handleAddBuyNowToBundle}
              onBundleCheckout={handleBundleCheckout}
              onBid={handleBid}
              onLoadBidHistory={handleLoadAuctionBids}
              onQuickCheckout={handleQuickCheckoutBuyNow}
              onSectionChange={navigateSection}
              onToggleWatchlist={handleToggleWatchlist}
              orderRows={liveOrders}
              summaryCards={liveSummaryCards}
            />
          ) : (
            <SectionView
              section={activeSection}
              title={activeLabel}
              auctions={visibleAuctions}
              buyNowItems={liveBuyNowItems}
              bundleRows={liveBundleRows}
              bundleSummary={bundleData}
              consignments={liveConsignments}
              notifications={liveNotifications}
              onOpenNotification={handleOpenNotification}
              paymentProvider={dashboardData?.paymentProvider}
              vaultItems={liveVaultItems}
              vaultSummary={liveVaultSummary}
              onAddPaymentMethod={handleAddPaymentMethod}
              onCreatePaymentSetup={handleCreatePaymentSetup}
              onCompletePaymentSetup={handleCompletePaymentSetup}
              onRemovePaymentMethod={handleRemovePaymentMethod}
              onSetPrimaryPaymentMethod={handleSetPrimaryPaymentMethod}
              onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
              onMarkNotificationRead={handleMarkNotificationRead}
              onOpenNotification={handleOpenNotification}
              onPayOrder={handlePayOrder}
              onAddToBundle={handleAddBuyNowToBundle}
              onBundleCheckout={handleBundleCheckout}
              onCreateConsignment={handleCreateConsignment}
              onBid={handleBid}
              onLoadBidHistory={handleLoadAuctionBids}
              onQuickCheckout={handleQuickCheckoutBuyNow}
              onSectionChange={navigateSection}
              onToggleWatchlist={handleToggleWatchlist}
              onUpdateNotificationPreferences={handleUpdateNotificationPreferences}
              onUpdateProfile={handleUpdateProfile}
              orderRows={liveOrders}
              account={liveAccount}
            />
          )}
        </div>
      </main>
    </section>
  )
}

function PaymentCardPrompt({ account, onClose, onSetup }) {
  return (
    <div className="obc-payment-prompt" role="dialog" aria-modal="true" aria-labelledby="obc-payment-prompt-title">
      <div className="obc-payment-prompt__backdrop" onClick={onClose} />
      <section className="obc-payment-prompt__panel">
        <button className="obc-payment-prompt__close" type="button" aria-label="Dismiss payment setup prompt" onClick={onClose}>
          <FaXmark />
        </button>
        <span className="obc-payment-prompt__icon"><FaCreditCard /></span>
        <div>
          <span className="obc-payment-prompt__eyebrow">Bidder setup</span>
          <h2 id="obc-payment-prompt-title">Add a payment card to unlock bidding</h2>
          <p>
            {account?.name ? `${account.name}, ` : ''}
            save a primary card before placing auction bids, using quick checkout, or paying weekly bundle holds.
          </p>
        </div>
        <div className="obc-payment-prompt__checks">
          <span><FaShieldHalved /> Verified bidder status</span>
          <span><FaGavel /> Auction bid access</span>
          <span><FaCartShopping /> Faster checkout</span>
        </div>
        <div className="obc-payment-prompt__actions">
          <button className="obc-dashboard__bid-button" type="button" onClick={onSetup}>Add Card Now</button>
          <button type="button" onClick={onClose}>Later</button>
        </div>
      </section>
    </div>
  )
}

function DashboardHeader({ activeLabel, activeSection, onSectionChange }) {
  return (
    <section className="obc-dashboard__hero obc-dashboard__hero--functional">
      <img src={background} alt="" aria-hidden="true" />
      <div className="obc-dashboard__hero-shade" />
      <div className="scanline-overlay" />
      <div className="obc-dashboard__hero-content">
        <span className="obc-dashboard__kicker">
          <FaShieldHalved />
          {activeSection === 'overview' ? 'Verified bidder active' : 'User dashboard module'}
        </span>
        <h1>
          {activeLabel}
          <span>{activeSection === 'overview' ? 'Command Center' : 'Control Panel'}</span>
        </h1>
        <p>{sectionDescriptions[activeSection]}</p>
      </div>
      <div className="obc-dashboard__hero-actions">
        <button type="button" onClick={() => onSectionChange('auctions')}>Place Bid</button>
        <button type="button" onClick={() => onSectionChange('bundle')}>Checkout Bundle</button>
        <button type="button" onClick={() => onSectionChange('payments')}>Verify Payment</button>
      </div>
    </section>
  )
}

function DashboardSkeleton({ activeSection }) {
  return (
    <div className="obc-dashboard__grid obc-dashboard__skeleton" aria-live="polite" aria-busy="true">
      <div className="obc-dashboard__primary">
        <section className="obc-dashboard__summary-grid">
          {Array.from({ length: 4 }, (_, index) => (
            <article className="tactical-card obc-dashboard__summary-card obc-skeleton-card" key={`summary-skeleton-${index}`}>
              <span className="obc-skeleton-icon" />
              <div>
                <span className="obc-skeleton-line is-short" />
                <strong className="obc-skeleton-line is-value" />
                <small className="obc-skeleton-line" />
              </div>
            </article>
          ))}
        </section>

        <section className="obc-dashboard__quickbar obc-dashboard__quickbar--functional">
          <div className="tactical-card obc-dashboard__balance obc-skeleton-card">
            <span className="obc-skeleton-line is-short" />
            <strong className="obc-skeleton-line is-value" />
            <span className="obc-skeleton-line" />
          </div>
          <div className="obc-dashboard__quick-actions">
            {Array.from({ length: 4 }, (_, index) => (
              <div className="tactical-card obc-skeleton-card" key={`quick-skeleton-${index}`}>
                <span className="obc-skeleton-icon" />
                <span className="obc-skeleton-line" />
              </div>
            ))}
          </div>
        </section>

        <DashboardListSkeleton title={activeSection === 'shop' ? 'Loading Buy Now shop' : 'Loading auction lane'} cards={4} withImage />
      </div>

      <aside className="obc-dashboard__secondary">
        <DashboardListSkeleton title="Loading weekly bundle" cards={3} />
        <DashboardListSkeleton title="Loading orders" cards={3} />
        <DashboardListSkeleton title="Loading notifications" cards={4} />
      </aside>
    </div>
  )
}

function DashboardListSkeleton({ cards = 3, title, withImage = false }) {
  return (
    <section className="tactical-card obc-dashboard__skeleton-panel">
      <div className="obc-dashboard__side-head">
        <h3>{title}</h3>
        <span className="obc-skeleton-line is-short" />
      </div>
      <div className="obc-dashboard__skeleton-list">
        {Array.from({ length: cards }, (_, index) => (
          <article key={`${title}-${index}`}>
            {withImage ? <span className="obc-skeleton-thumb" /> : null}
            <div>
              <span className="obc-skeleton-line is-title" />
              <span className="obc-skeleton-line" />
              <span className="obc-skeleton-line is-short" />
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

function Overview({ auctions, buyNowItems, bundleRows, bundleSummary, notifications, onAddToBundle, onBid, onBundleCheckout, onLoadBidHistory, onMarkAllNotificationsRead, onMarkNotificationRead, onOpenNotification, onPayOrder, onQuickCheckout, onSectionChange, onToggleWatchlist, orderRows, summaryCards }) {
  const bundleDue = formatMoney(bundleSummary?.totalDue || 0)
  const auctionPreview = auctions.slice(0, 4)
  const buyNowPreview = buyNowItems.slice(0, 4)
  const bundlePreview = bundleRows.slice(0, 4)
  const orderPreview = orderRows.slice(0, 4)
  const notificationPreview = notifications.slice(0, 4)

  return (
    <div className="obc-dashboard__grid">
      <div className="obc-dashboard__primary">
        <section className="obc-dashboard__summary-grid">
          {summaryCards.map((card) => (
            <article className={`tactical-card obc-dashboard__summary-card is-${card.tone}`} key={card.label}>
              {card.icon}
              <div>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <small>{card.note}</small>
              </div>
            </article>
          ))}
        </section>

        <section className="obc-dashboard__quickbar obc-dashboard__quickbar--functional">
          <div className="tactical-card obc-dashboard__balance">
            <span>Bundle due</span>
            <strong>{bundleDue}</strong>
            <button type="button" onClick={() => onSectionChange('payments')}>
              <FaPlus />
              Payment
            </button>
          </div>
          <div className="obc-dashboard__quick-actions">
            {[
              ['auctions', 'Bid Now', <FaGavel />],
              ['shop', 'Buy Now', <FaCartShopping />],
              ['vault', 'Open Vault', <FaShieldHalved />],
              ['bundle', 'Friday Checkout', <FaBox />],
            ].map(([id, label, icon]) => (
              <button className="tactical-card" type="button" onClick={() => onSectionChange(id)} key={id}>
                {icon}
                <span>{label}</span>
              </button>
            ))}
          </div>
        </section>

        <AuctionSection compact lots={auctionPreview} onBid={onBid} onLoadBidHistory={onLoadBidHistory} onToggleWatchlist={onToggleWatchlist} onSectionChange={onSectionChange} />
        <BuyNowSection compact items={buyNowPreview} onAddToBundle={onAddToBundle} onQuickCheckout={onQuickCheckout} onSectionChange={onSectionChange} />
      </div>

      <aside className="obc-dashboard__secondary">
        <WeeklyBundle rows={bundlePreview} summary={bundleSummary} onCheckout={onBundleCheckout} onSectionChange={onSectionChange} />
        <OrdersPanel rows={orderPreview} onPayOrder={onPayOrder} onSectionChange={onSectionChange} />
        <NotificationsPanel notices={notificationPreview} onMarkAllRead={onMarkAllNotificationsRead} onMarkRead={onMarkNotificationRead} onOpenNotice={onOpenNotification} onSectionChange={onSectionChange} />
      </aside>
    </div>
  )
}

function SectionView({ account, section, title, auctions, buyNowItems, bundleRows, bundleSummary, consignments, notifications, paymentProvider, vaultItems, vaultSummary, onAddPaymentMethod, onAddToBundle, onBid, onBundleCheckout, onCompletePaymentSetup, onCreateConsignment, onCreatePaymentSetup, onLoadBidHistory, onMarkAllNotificationsRead, onMarkNotificationRead, onOpenNotification, onPayOrder, onQuickCheckout, onRemovePaymentMethod, onSectionChange, onSetPrimaryPaymentMethod, onToggleWatchlist, onUpdateNotificationPreferences, onUpdateProfile, orderRows }) {
  return (
    <div className="obc-dashboard__section-view">
      {section === 'auctions' || section === 'watchlist' ? <AuctionSection lots={auctions} onBid={onBid} onLoadBidHistory={onLoadBidHistory} onToggleWatchlist={onToggleWatchlist} title={title} /> : null}
      {section === 'vault' ? <VaultPanel account={account} ImageComponent={DashboardImage} items={vaultItems} summary={vaultSummary} onSectionChange={onSectionChange} /> : null}
      {section === 'shop' ? <BuyNowSection items={buyNowItems} onAddToBundle={onAddToBundle} onQuickCheckout={onQuickCheckout} /> : null}
      {section === 'bundle' ? <WeeklyBundle expanded rows={bundleRows} summary={bundleSummary} onCheckout={onBundleCheckout} onSectionChange={onSectionChange} /> : null}
      {section === 'orders' ? <OrdersPanel expanded rows={orderRows} onPayOrder={onPayOrder} onSectionChange={onSectionChange} /> : null}
      {section === 'consignments' ? <ConsignmentsPanel rows={consignments} onCreateConsignment={onCreateConsignment} onSectionChange={onSectionChange} /> : null}
      {section === 'payments' ? <PaymentsPanel account={account} paymentProvider={paymentProvider} onAddPaymentMethod={onAddPaymentMethod} onCreatePaymentSetup={onCreatePaymentSetup} onCompletePaymentSetup={onCompletePaymentSetup} onRemovePaymentMethod={onRemovePaymentMethod} onSetPrimaryPaymentMethod={onSetPrimaryPaymentMethod} /> : null}
      {section === 'profile' ? <ProfilePanel account={account} onUpdateProfile={onUpdateProfile} /> : null}
      {section === 'alerts' ? <NotificationsPanel expanded notices={notifications} onMarkAllRead={onMarkAllNotificationsRead} onMarkRead={onMarkNotificationRead} onOpenNotice={onOpenNotification} onSectionChange={onSectionChange} /> : null}
      {section === 'settings' ? <SettingsPanel account={account} onUpdateNotificationPreferences={onUpdateNotificationPreferences} onUpdateProfile={onUpdateProfile} /> : null}
    </div>
  )
}

function AuctionSection({ compact = false, lots, onBid, onLoadBidHistory, onToggleWatchlist, onSectionChange, title = 'Auction Center' }) {
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedLot, setSelectedLot] = useState(null)
  const [bidAmount, setBidAmount] = useState('')
  const [modalStatus, setModalStatus] = useState('')
  const [bidLedger, setBidLedger] = useState([])
  const [bidLedgerStatus, setBidLedgerStatus] = useState('')
  const [isSubmittingBid, setIsSubmittingBid] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)

    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!selectedLot) return

    const latestLot = lots.find((lot) => lot.id === selectedLot.id)

    if (latestLot && latestLot !== selectedLot) {
      setSelectedLot(latestLot)
      setBidAmount(String(latestLot.nextBidAmount || ''))
    }
  }, [lots, selectedLot])

  useEffect(() => {
    if (!selectedLot?.id || !onLoadBidHistory) {
      setBidLedger([])
      setBidLedgerStatus('')
      return undefined
    }

    let active = true

    async function loadBidLedger(showLoading = false) {
      if (showLoading) setBidLedgerStatus('Loading live bid activity...')

      try {
        const result = await onLoadBidHistory(selectedLot)
        if (!active) return
        setBidLedger(result?.bids || [])
        setBidLedgerStatus('')
      } catch (error) {
        if (!active) return
        setBidLedger([])
        setBidLedgerStatus(error.message || 'Could not load recent bid activity.')
      }
    }

    loadBidLedger(true)
    const refresh = window.setInterval(() => loadBidLedger(false), 10000)

    return () => {
      active = false
      window.clearInterval(refresh)
    }
  }, [selectedLot?.id, onLoadBidHistory])

  const metrics = useMemo(() => {
    const live = lots.filter((lot) => ['Live', 'Leading'].includes(lot.status)).length
    const watched = lots.filter((lot) => lot.isWatched).length
    const leading = lots.filter((lot) => lot.status === 'Leading').length
    const bids = lots.reduce((sum, lot) => sum + Number(lot.bids || 0), 0)

    return [
      { label: 'Live lots', value: live },
      { label: 'Watching', value: watched },
      { label: 'Leading', value: leading },
      { label: 'Room bids', value: bids },
    ]
  }, [lots])

  const filteredLots = useMemo(() => {
    const cleanQuery = query.trim().toLowerCase()

    return lots.filter((lot) => {
      const effectiveStatus = getEffectiveAuctionStatus(lot, now)
      const displayStatus = formatEffectiveDashboardStatus(lot, now)
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'watched' ? lot.isWatched : displayStatus.toLowerCase() === statusFilter || effectiveStatus === statusFilter)
      const matchesQuery = !cleanQuery || [lot.name, lot.detail, lot.type, lot.category]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(cleanQuery))

      return matchesStatus && matchesQuery
    })
  }, [lots, now, query, statusFilter])

  const openBidModal = (lot) => {
    setSelectedLot(lot)
    setBidAmount(String(lot.nextBidAmount || ''))
    setModalStatus('')
    setBidLedger([])
    setBidLedgerStatus('Loading live bid activity...')
  }

  const closeBidModal = (force = false) => {
    if (isSubmittingBid && !force) return
    setSelectedLot(null)
    setBidAmount('')
    setModalStatus('')
    setBidLedger([])
    setBidLedgerStatus('')
  }

  const submitBid = async (event) => {
    event.preventDefault()
    if (!selectedLot) return

    const amount = Number(bidAmount)

    if (!Number.isFinite(amount) || amount < selectedLot.nextBidAmount) {
      setModalStatus(`Minimum bid is ${selectedLot.nextBid}.`)
      return
    }

    if (!dashboardAuctionCanBid(selectedLot, now)) {
      setModalStatus('This auction is not currently accepting bids.')
      return
    }

    setIsSubmittingBid(true)
    setModalStatus('Submitting bid...')
    const accepted = await onBid?.(selectedLot, amount)
    setIsSubmittingBid(false)

    if (accepted) {
      closeBidModal(true)
    } else {
      setModalStatus('Bid was not accepted. Review account verification, auction status, or bid amount.')
    }
  }

  const selectedClock = selectedLot ? getDashboardAuctionClock(selectedLot, now) : null
  const selectedClockSegments = selectedLot ? getDashboardCountdownSegments(selectedLot, now) : []
  const selectedDisplayStatus = selectedLot ? formatEffectiveDashboardStatus(selectedLot, now) : ''

  return (
    <section className={`obc-dashboard__auctions obc-dashboard__auction-center ${compact ? 'is-overview' : 'is-section'}`}>
      <div className="obc-dashboard__section-head obc-dashboard__auction-center-head">
        <div>
          <span />
          <h2>{title}</h2>
          <p>Track active lots, watchlist status, bid pressure, and place confirmed bids from a review modal.</p>
        </div>
        <div className="obc-dashboard__section-actions">
          {compact ? <button type="button" onClick={() => onSectionChange?.('auctions')}>View all</button> : null}
          <button type="button" className="px-2" onClick={() => setStatusFilter('watched')}>Watchlist</button>
        </div>
      </div>

      <div className="obc-dashboard__auction-summary">
        {metrics.map((metric) => (
          <article key={metric.label}>
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
          </article>
        ))}
      </div>

      <div className="obc-dashboard__auction-controls" aria-label="Auction dashboard filters">
        <label>
          <FaMagnifyingGlass />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search player, set, type, category..."
          />
        </label>
        <label>
          <FaFilter />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter auction status">
            <option value="all">All auction lots</option>
            <option value="live">Live lots</option>
            <option value="leading">Leading</option>
            <option value="scheduled">Scheduled</option>
            <option value="watched">Watchlist</option>
            <option value="closed">Closed</option>
            <option value="won">Won</option>
          </select>
        </label>
      </div>

      <div className="obc-dashboard__auction-grid main-paige">
        {filteredLots.map((card) => {
          const canOpenBid = dashboardAuctionCanBid(card, now)
          const displayStatus = formatEffectiveDashboardStatus(card, now)
          const clock = getDashboardAuctionClock(card, now)
          const clockSegments = getDashboardCountdownSegments(card, now)

          return (
          <article className={`tactical-card obc-dashboard__auction-card is-${displayStatus.toLowerCase().replace(/\s+/g, '-')}`} key={card.name}>
            <div className="obc-dashboard__auction-image">
              <DashboardImage src={card.image} alt={card.name} />
              <b>{displayStatus}</b>
              <button className={card.isWatched ? 'is-watched' : undefined} type="button" aria-label={`${card.isWatched ? 'Remove' : 'Watch'} ${card.name}`} onClick={() => onToggleWatchlist?.(card)}>
                <FaHeart />
              </button>
            </div>
            <div className="obc-dashboard__auction-body">
              <h3>{card.name}</h3>
              <p>{card.detail}</p>
              <div className="obc-dashboard__lot-tags">
                <span>{card.type}</span>
                <span>{card.category}</span>
                <span>{displayStatus}</span>
                <span>{card.reserveMet ? 'Reserve met' : 'Reserve open'}</span>
              </div>
              <div className="obc-dashboard__auction-meta">
                <span>
                  <small>Current Bid</small>
                  <strong>{card.bid}</strong>
                </span>
                <span>
                  <small>{displayStatus === 'Won' ? 'Action' : 'Next Bid'}</small>
                  <strong>{card.nextBid}</strong>
                </span>
              </div>
              <div className="obc-dashboard__auction-rules">
                <span><small>Increment</small><strong>{formatMoney(card.minimumIncrement)}</strong></span>
                <span><small>Reserve</small><strong>{card.reserveMet ? 'Met' : card.reserve}</strong></span>
                <span><small>Bids</small><strong>{card.bids}</strong></span>
              </div>
              {card.userLatestBid ? (
                <div className="obc-dashboard__recent-bid">
                  <small>Your latest bid</small>
                  <strong>{card.userLatestBid.amount}</strong>
                  <span>{card.userLatestBid.extendedClose ? 'Timer extended' : card.userLatestBid.at}</span>
                </div>
              ) : null}
              <div className={`obc-dashboard__timer obc-dashboard__timer--live obc-dashboard__card-clock is-${clock.tone}`}>
                <div className="obc-dashboard__card-clock-head">
                  <span><FaClock /> {clock.label}</span>
                  <strong>{clock.value}</strong>
                </div>
                <div className="obc-dashboard__card-clock-grid" aria-label={`${clock.label} ${clock.value}`}>
                  {clockSegments.map((segment) => (
                    <span key={segment.label}>
                      <strong>{segment.value}</strong>
                      <small>{segment.label}</small>
                    </span>
                  ))}
                </div>
              </div>
              <button className="obc-dashboard__bid-button" type="button" disabled={!canOpenBid} onClick={() => openBidModal(card)}>
                {displayStatus === 'Won' ? 'Won' : displayStatus === 'Scheduled' ? 'Scheduled' : displayStatus === 'Closed' || displayStatus === 'Finalizing' ? displayStatus : displayStatus === 'Leading' ? 'Raise Bid' : 'Open Bid Card'}
              </button>
            </div>
          </article>
          )
        })}
      </div>
      {!filteredLots.length ? <p className="obc-dashboard__empty-state">No auctions match this view yet.</p> : null}

      {selectedLot ? (
        <div className="obc-bid-modal" role="dialog" aria-modal="true" aria-labelledby="obc-bid-modal-title">
          <div className="obc-bid-modal__backdrop" onClick={closeBidModal} />
          <section className="obc-bid-modal__panel">
            <button className="obc-bid-modal__close" type="button" aria-label="Close bid card" onClick={closeBidModal}>
              <FaXmark />
            </button>
            <header className="obc-bid-modal__header">
              <div className="obc-bid-modal__media">
                <DashboardImage src={selectedLot.image} alt={selectedLot.name} />
                <span>{selectedDisplayStatus}</span>
              </div>
              <div className="obc-bid-modal__title">
                <span className="obc-bid-modal__eyebrow"><FaShieldHalved /> Verified bid ticket</span>
                <h2 id="obc-bid-modal-title">{selectedLot.name}</h2>
                <p>{selectedLot.detail || 'Review current room activity and confirm your next live-style bid.'}</p>
                <div>
                  <span>{selectedLot.type}</span>
                  <span>{selectedLot.category}</span>
                  <span>{selectedLot.reserveMet ? 'Reserve met' : 'Reserve open'}</span>
                </div>
              </div>
            </header>

            <form className="obc-bid-modal__content" onSubmit={submitBid}>
              <section className={`obc-bid-modal__clock is-${selectedClock.tone}`}>
                <div className="obc-bid-modal__clock-head">
                  <span>
                    <small>{selectedClock.label}</small>
                    <strong>{selectedClock.value}</strong>
                  </span>
                  <span>
                    <small>Scheduled close</small>
                    <strong>{selectedLot.endsLabel || selectedLot.timer}</strong>
                  </span>
                </div>
                <div className="obc-bid-modal__clock-grid" aria-label={`${selectedClock.label} ${selectedClock.value}`}>
                  {selectedClockSegments.map((segment) => (
                    <span key={segment.label}>
                      <strong>{segment.value}</strong>
                      <small>{segment.label}</small>
                    </span>
                  ))}
                </div>
              </section>

              <div className="obc-bid-modal__workspace">
                <section className="obc-bid-modal__bid-card">
                  <div className="obc-bid-modal__stats">
                    <article><small>Current bid</small><strong>{selectedLot.bid}</strong></article>
                    <article><small>Minimum next</small><strong>{selectedLot.nextBid}</strong></article>
                    <article><small>Increment</small><strong>{formatMoney(selectedLot.minimumIncrement)}</strong></article>
                    <article><small>Reserve</small><strong>{selectedLot.reserveMet ? 'Met' : selectedLot.reserve}</strong></article>
                  </div>

                  <label className="obc-bid-modal__amount">
                    Bid amount
                    <input
                      type="number"
                      min={selectedLot.nextBidAmount || 0}
                      step={selectedLot.minimumIncrement || 1}
                      value={bidAmount}
                      onChange={(event) => setBidAmount(event.target.value)}
                      autoFocus
                      required
                    />
                  </label>

                  <div className="obc-bid-modal__quick">
                    {[0, 1, 2].map((multiplier) => {
                      const amount = selectedLot.nextBidAmount + selectedLot.minimumIncrement * multiplier
                      return (
                        <button type="button" key={amount} onClick={() => setBidAmount(String(amount))}>
                          {formatMoney(amount)}
                        </button>
                      )
                    })}
                  </div>

                  <div className="obc-bid-modal__notice">
                    <FaTriangleExclamation />
                    <span>Bids are live-style and cannot be lowered after submission. Anti-snipe lots may extend near close.</span>
                  </div>

                  {modalStatus ? <p className="obc-bid-modal__status">{modalStatus}</p> : null}

                  <div className="obc-bid-modal__actions">
                    <button type="button" onClick={closeBidModal} disabled={isSubmittingBid}>Cancel</button>
                    <button className="obc-dashboard__bid-button" type="submit" disabled={isSubmittingBid}>
                      {isSubmittingBid ? 'Submitting...' : 'Confirm Bid'}
                    </button>
                  </div>
                </section>

                <aside className="obc-bid-modal__ledger">
                  <div>
                    <strong>Recent live bids</strong>
                    <span>Refreshes while open</span>
                  </div>
                  <div className="obc-bid-modal__ledger-list">
                    {bidLedger.slice(0, 5).map((bid) => (
                      <article key={bid.id}>
                        <span>{bid.bidderLabel || 'Bidder'}</span>
                        <strong>{formatMoney(bid.amount)}</strong>
                        <small>{bid.extendedClose ? 'Timer extended' : formatDashboardBidTime(bid.createdAt)}</small>
                      </article>
                    ))}
                    {!bidLedger.length ? <p>{bidLedgerStatus || 'No room bids recorded yet.'}</p> : null}
                  </div>
                </aside>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </section>
  )
}

function BuyNowSection({ compact = false, items = [], onAddToBundle, onQuickCheckout, onSectionChange }) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All')
  const categories = useMemo(() => ['All', ...Array.from(new Set(items.map((item) => item.category || item.detail).filter(Boolean)))], [items])
  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase()

    return items.filter((item) => {
      const matchesCategory = category === 'All' || item.category === category || item.detail === category
      const matchesSearch = !query || [item.name, item.detail, item.category].filter(Boolean).some((value) => value.toLowerCase().includes(query))

      return matchesCategory && matchesSearch
    })
  }, [category, items, search])

  return (
    <section className="tactical-card obc-dashboard__buy-now">
      <div className="obc-dashboard__side-head">
        <div>
          <h3>Buy Now Shop</h3>
          <span>{filteredItems.length} listings</span>
        </div>
        <button type="button" onClick={() => onSectionChange?.('shop')}>View all</button>
      </div>
      <div className="obc-dashboard__buy-controls">
        <label>
          <FaMagnifyingGlass />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search cards, sets, memorabilia"
          />
        </label>
        <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Filter Buy Now category">
          {categories.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </div>
      <div className={`obc-dashboard__buy-grid ${compact ? 'is-compact' : ''}`}>
        {filteredItems.map((item) => (
          <article key={item.name}>
            <DashboardImage src={item.image} alt={item.name} />
            <div>
              <h4>{item.name}</h4>
              <p>{item.detail}</p>
              <strong>{item.price}</strong>
              <small>{item.heldByUser ? 'Already in your orders' : item.available === false ? 'Sold out' : item.inventory ? `${item.inventory} in stock` : 'Limited stock'}</small>
            </div>
            <div className="obc-dashboard__buy-actions">
              <button type="button" onClick={() => onAddToBundle?.(item)} disabled={item.bundleEligible === false || item.available === false}>
                {item.heldByUser ? 'In Bundle' : item.bundleEligible === false ? 'No Bundle' : 'Add Bundle'}
              </button>
              <button type="button" onClick={() => onQuickCheckout?.(item)} disabled={item.available === false || item.heldByUser}>Quick Pay</button>
            </div>
          </article>
        ))}
      </div>
      {!filteredItems.length ? <p className="obc-dashboard__empty-state">No Buy Now listings match this filter.</p> : null}
    </section>
  )
}

function WeeklyBundle({ onCheckout, onSectionChange, rows = [], summary, expanded = false }) {
  const itemCount = summary?.items?.length || rows.length
  const subtotal = formatMoney(summary?.subtotal || rows.reduce((sum, row) => sum + Number(String(row.price).replace(/[^0-9.]/g, '') || 0), 0))
  const shipping = formatMoney(summary?.estimatedShipping || 0)
  const totalDue = formatMoney(summary?.totalDue || 0)
  const canCheckout = Boolean(summary?.canCheckoutNow ?? rows.length)
  const shippingBreakdown = summary?.shipping
  const readiness = summary?.readiness || []
  const checkoutMode = shippingBreakdown?.mode === 'fixed' ? 'Fixed shipping MVP' : 'Calculated shipping'
  const readinessItems = readiness.length ? readiness : [
    { code: 'bundle-loaded', ready: true, label: 'Checkout data loaded' },
    { code: 'bundle-action', ready: canCheckout, label: canCheckout ? 'Manual checkout available' : 'Add eligible items to the bundle' },
    { code: 'bundle-mode', ready: true, label: checkoutMode },
  ]
  const previewRows = rows.slice(0, 2)

  if (!expanded) {
    return (
      <section className="tactical-card obc-dashboard__weekly obc-dashboard__weekly--functional obc-dashboard__weekly--compact">
        <div className="obc-dashboard__side-head">
          <div>
            <h3>Weekly Bundle</h3>
            <p>{itemCount} held item{itemCount === 1 ? '' : 's'} / {summary?.checkoutDate || 'Friday'} checkout</p>
          </div>
          <button type="button" onClick={() => onSectionChange('bundle')}>View all</button>
        </div>

        <div className="obc-dashboard__weekly-compact-total">
          <span>Total due</span>
          <strong>{totalDue}</strong>
          <small>{checkoutMode} / {shipping} shipping</small>
        </div>

        <div className="obc-dashboard__weekly-compact-list">
          {previewRows.map((row) => (
            <article key={row.id || row.item}>
              <div>
                <span>{row.item}</span>
                <small>{row.status} / Due {row.due || summary?.checkoutDate || 'Friday'}</small>
              </div>
              <strong>{row.price}</strong>
            </article>
          ))}
          {!previewRows.length ? <p className="obc-dashboard__empty-state">No items held for bundle checkout.</p> : null}
          {rows.length > previewRows.length ? <small className="obc-dashboard__weekly-more">+{rows.length - previewRows.length} more item{rows.length - previewRows.length === 1 ? '' : 's'}</small> : null}
        </div>

        <div className="obc-dashboard__weekly-compact-actions">
          <button type="button" onClick={onCheckout} disabled={!canCheckout}>Checkout</button>
          <button type="button" onClick={() => onSectionChange('orders')}>Orders</button>
        </div>
      </section>
    )
  }

  return (
    <section className={`tactical-card obc-dashboard__weekly obc-dashboard__weekly--functional ${expanded ? 'is-expanded' : ''}`}>
      <div className="obc-dashboard__side-head">
        <div>
          <h3>Weekly Bundle</h3>
          <p>Friday combined checkout for held buys, unpaid wins, and manual checkout.</p>
        </div>
        <div className="obc-dashboard__bundle-head-actions">
          <button type="button" onClick={() => onSectionChange('orders')}>Orders</button>
          <button type="button" onClick={onCheckout} disabled={!canCheckout}>Checkout now</button>
        </div>
      </div>

      <div className="obc-dashboard__bundle-layout">
        <div className="obc-dashboard__bundle-main">
          <div className="obc-dashboard__bundle-hero">
            <div className="obc-dashboard__bundle-status">
              <FaTruckFast />
              <div>
                <strong>Friday combined checkout</strong>
                <span>{itemCount} held item{itemCount === 1 ? '' : 's'} / target {summary?.checkoutDate || 'Friday'}</span>
              </div>
            </div>
            <div className="obc-dashboard__bundle-meta-row">
              <article>
                <span>Subtotal</span>
                <strong>{subtotal}</strong>
              </article>
              <article>
                <span>Shipping</span>
                <strong>{shipping}</strong>
              </article>
              <article>
                <span>Total due</span>
                <strong>{totalDue}</strong>
              </article>
            </div>
          </div>

          {shippingBreakdown ? (
            <div className="obc-dashboard__shipping-breakdown">
              <span>{shippingBreakdown.mode === 'fixed' ? 'Fixed shipping' : 'Shipping calculation'}</span>
              {shippingBreakdown.mode === 'fixed' ? (
                <strong>{formatMoney(shippingBreakdown.fixedPrice || shippingBreakdown.total || 0)} flat rate</strong>
              ) : (
                <>
                  <strong>{formatMoney(shippingBreakdown.baseRate || 0)} base</strong>
                  <strong>{formatMoney(shippingBreakdown.itemHandling || 0)} handling</strong>
                  <strong>{formatMoney(shippingBreakdown.protection || 0)} protection</strong>
                </>
              )}
            </div>
          ) : null}

          <div className="obc-dashboard__bundle-list-head">
            <div>
              <h4>Held items</h4>
              <span>{itemCount} item{itemCount === 1 ? '' : 's'} waiting for combined checkout</span>
            </div>
            <strong>{totalDue}</strong>
          </div>

          <div className="obc-dashboard__table-list">
            {rows.map((row) => (
              <article key={row.id || row.item}>
                <div>
                  <span>{row.item}</span>
                  <small>{row.hold || 'Bundle item'} / {row.status} / {row.shipping || 'bundle hold'}</small>
                </div>
                <strong>{row.price}</strong>
                <small className="obc-dashboard__bundle-due">Due: {row.due || summary?.checkoutDate || 'Friday'}</small>
              </article>
            ))}
            {!rows.length ? <p className="obc-dashboard__empty-state">No items are currently held for weekly bundle checkout.</p> : null}
          </div>
        </div>

        <div className="obc-dashboard__bundle-panel">
          <div className="obc-dashboard__bundle-panel-head">
            <span>Checkout readiness</span>
            <strong>{canCheckout ? 'Ready to pay' : 'Action needed'}</strong>
          </div>
          <div className="obc-dashboard__bundle-readiness">
            {readinessItems.map((item) => (
              <article className={item.ready ? 'is-ready' : 'is-blocked'} key={item.code}>
                <strong>{item.ready ? 'Ready' : 'Action'}</strong>
                <span>{item.label}</span>
              </article>
            ))}
          </div>

          <div className="obc-dashboard__penalty-alert">
            <FaTriangleExclamation />
            <span>{canCheckout ? `Manual checkout is available now. Friday bundle total is ${totalDue}.` : 'Add eligible Buy Now items or unpaid wins to start a weekly bundle.'}</span>
          </div>

          <div className="obc-dashboard__bundle-checkout-card">
            <span>Total due</span>
            <strong>{totalDue}</strong>
            <small>{checkoutMode} / checkout target {summary?.checkoutDate || 'Friday'}</small>
            <button type="button" onClick={onCheckout} disabled={!canCheckout}>Manual Checkout Now</button>
          </div>
        </div>
      </div>
    </section>
  )
}

function ConsignmentsPanel({ rows = [], onCreateConsignment, onSectionChange }) {
  const [selectedFile, setSelectedFile] = useState(null)
  const submittedCount = rows.filter((row) => ['submitted', 'photos needed', 'ready for decision'].includes(String(row.status || '').toLowerCase())).length
  const approvedCount = rows.filter((row) => ['approved', 'scheduled auction', 'buy now listing', 'listed'].includes(String(row.sale || '').toLowerCase()) || row.canConvert).length
  const payoutPendingCount = rows.filter((row) => ['manual pending', 'manual-pending', 'pending'].includes(String(row.payout || '').toLowerCase()) || row.stageTone === 'danger').length
  const totalEstimate = rows.reduce((sum, row) => sum + Number(String(row.estimate).replace(/[^0-9.]/g, '') || 0), 0)
  const previewRows = rows.slice(0, 4)

  const handleSubmit = (event) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const itemTitle = String(form.get('itemTitle') || '').trim()

    if (!itemTitle) return

    onCreateConsignment?.({
      file: selectedFile,
      payload: {
        itemTitle,
        category: form.get('category') || 'Cards',
        estimate: Number(form.get('estimate') || 0),
        notes: form.get('notes') || '',
      },
    })
    event.currentTarget.reset()
    setSelectedFile(null)
  }

  return (
    <section className="tactical-card obc-dashboard__detail-panel">
      <div className="obc-dashboard__side-head">
        <div>
          <h3>Consignment Intake</h3>
          <span>{rows.length} records</span>
        </div>
        <button type="button" onClick={() => onSectionChange?.('orders')}>Related orders</button>
      </div>
      <div className="obc-consignment-dashboard">
        <div className="obc-consignment-dashboard__metrics">
          <article>
            <strong>{rows.length}</strong>
            <span>Consignments</span>
            <small>All intake records in the queue</small>
          </article>
          <article>
            <strong>{submittedCount}</strong>
            <span>Submitted</span>
            <small>Waiting for review or photos</small>
          </article>
          <article>
            <strong>{approvedCount}</strong>
            <span>Approved</span>
            <small>Ready for listing or sale</small>
          </article>
          <article>
            <strong>{formatMoney(totalEstimate)}</strong>
            <span>Estimated value</span>
            <small>{payoutPendingCount} payout{payoutPendingCount === 1 ? '' : 's'} pending</small>
          </article>
        </div>

        <div className="obc-consignment-dashboard__workspace">
          <form className="obc-consignment-form obc-consignment-form--panel" onSubmit={handleSubmit}>
            <div className="obc-consignment-form__head is-wide">
              <div>
                <h4>New intake</h4>
                <p>Capture consignor details and attach media for review.</p>
              </div>
            </div>
            <label>Item title<input name="itemTitle" placeholder="Card, sealed box, or memorabilia title" required /></label>
            <label>Category<select name="category" defaultValue="Cards"><option value="Cards">Cards</option><option value="Memorabilia">Memorabilia</option></select></label>
            <label>Estimated value<input name="estimate" placeholder="500" type="number" min="0" /></label>
            <label className="is-wide">Notes<input name="notes" placeholder="Condition, grading, provenance, payout notes..." /></label>
            <label className="obc-admin__file-button obc-consignment-upload">
              <FaCloudArrowUp />
              <span>
                <strong>{selectedFile ? selectedFile.name : 'Upload photo'}</strong>
                <small>{selectedFile ? 'Image selected for intake' : 'PNG, JPG, WEBP, or GIF'}</small>
              </span>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} />
            </label>
            <button className="obc-dashboard__bid-button" type="submit">Submit For Review</button>
          </form>

          <div className="obc-consignment-list obc-consignment-list--panel">
            <div className="obc-consignment-list__head">
              <div>
                <h4>Recent consignments</h4>
                <span>{previewRows.length} shown of {rows.length}</span>
              </div>
            </div>

            {previewRows.map((row) => (
              <article key={row.id}>
                <DashboardImage src={row.image} alt={row.item} />
                <div>
                  <strong>{row.item}</strong>
                  <span>{row.estimate} estimate</span>
                  <small><b className={`obc-order-stage is-${row.stageTone || 'neutral'}`}>{row.stage || row.status}</b> {row.status} / {row.sale} / {row.payout}</small>
                  <small>Sale: {row.saleAmount} / Payout: {row.payoutAmount} / Expected: {row.expectedPayoutAmount}</small>
                  <small>Source: {row.source} / Next: {row.nextAction}</small>
                  <small>Paid: {row.paidAt}</small>
                </div>
                <ConsignmentTimeline steps={row.timeline} />
              </article>
            ))}
            {!previewRows.length ? <p className="obc-dashboard__empty-state">No consignments yet.</p> : null}
          </div>
        </div>
      </div>
    </section>
  )
}

function ConsignmentTimeline({ steps = [] }) {
  if (!steps.length) return null

  return (
    <div className="obc-consignment-timeline">
      {steps.map((step) => (
        <span className={`is-${step.state}`} key={step.code}>{step.label}</span>
      ))}
    </div>
  )
}

function ProfilePanel({ account, onUpdateProfile, onSectionChange }) {
  const shipping = account?.shippingProfile || {}
  const preferences = {
    ...defaultNotificationPreferences,
    ...(account?.notificationPreferences || {}),
  }
  const activePreferences = Object.values(preferences).filter(Boolean).length
  const accountInitials = getAccountInitials(account?.name)
  const shippingReady = Boolean(shipping.ready || (shipping.line1 && shipping.city && shipping.state && shipping.postalCode))

  const handleSubmit = (event) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    onUpdateProfile?.({
      name: form.get('name') || account?.name || 'Collector',
      shippingProfile: {
        name: form.get('shippingName') || form.get('name') || account?.name || 'Collector',
        line1: form.get('line1') || '',
        line2: form.get('line2') || '',
        city: form.get('city') || '',
        state: form.get('state') || '',
        postalCode: form.get('postalCode') || '',
        country: form.get('country') || 'US',
        phone: form.get('phone') || '',
      },
    })
  }

  return (
    <section className="tactical-card obc-dashboard__detail-panel">
      <div className="obc-dashboard__side-head obc-profile-panel__head">
        <div>
          <h3>Profile & Shipping</h3>
          <span>Account details, shipping address, and bidder identity</span>
        </div>
        <div className="obc-dashboard__section-actions">
          <button type="button" onClick={() => onSectionChange?.('payments')}>Payments</button>
          <button type="button" onClick={() => onSectionChange?.('settings')}>Settings</button>
        </div>
      </div>
      <div className="obc-profile-panel__hero">
        <div className="obc-profile-panel__identity">
          <div className="obc-profile-panel__avatar" aria-hidden="true">{accountInitials}</div>
          <div>
            <span className="obc-profile-panel__eyebrow">Collector profile</span>
            <h4>{account?.name || 'Collector account'}</h4>
            <p>{account?.email || 'Email will appear here once the account is connected.'}</p>
          </div>
        </div>
        <div className="obc-profile-panel__status-grid">
          <article className={shippingReady ? 'is-ready' : 'is-blocked'}>
            <strong>{shippingReady ? 'Ready' : 'Needs setup'}</strong>
            <span>Shipping</span>
          </article>
          <article className={account?.verifiedBidder ? 'is-ready' : 'is-blocked'}>
            <strong>{account?.verifiedBidder ? 'Verified' : 'Pending'}</strong>
            <span>Bidder card</span>
          </article>
          <article>
            <strong>{activePreferences}</strong>
            <span>Alerts active</span>
          </article>
        </div>
      </div>
      <div className="obc-profile-panel__layout">
        <form className="obc-profile-panel__form" onSubmit={handleSubmit}>
          <div className="obc-profile-panel__section">
            <h4>Account info</h4>
            <p>Update the name used in the dashboard header and on marketplace records.</p>
          </div>
          <label>Display name<input name="name" defaultValue={account?.name || ''} placeholder="Collector name" required /></label>
          <label>Shipping name<input name="shippingName" defaultValue={shipping.name || account?.name || ''} placeholder="Full legal name" required /></label>

          <div className="obc-profile-panel__section">
            <h4>Shipping address</h4>
            <p>Use the exact shipping address for orders, bundle checkout, and winnings.</p>
          </div>
          <label className="is-wide">Address line 1<input name="line1" defaultValue={shipping.line1 || ''} placeholder="Street address" required /></label>
          <label className="is-wide">Address line 2<input name="line2" defaultValue={shipping.line2 || ''} placeholder="Apartment, suite, unit" /></label>
          <label>City<input name="city" defaultValue={shipping.city || ''} placeholder="City" required /></label>
          <label>State<input name="state" defaultValue={shipping.state || ''} placeholder="State" required /></label>
          <label>Postal code<input name="postalCode" defaultValue={shipping.postalCode || ''} placeholder="ZIP / postal code" required /></label>
          <label>Country<input name="country" defaultValue={shipping.country || 'US'} placeholder="US" required /></label>
          <label className="is-wide">Phone<input name="phone" defaultValue={shipping.phone || ''} placeholder="Phone for carrier contact" required /></label>
          <button className="obc-dashboard__bid-button" type="submit">Save Profile</button>
        </form>

        <aside className="obc-profile-panel__sidebar">
          <article className="obc-profile-panel__card">
            <span>Account snapshot</span>
            <strong>{account?.email || 'No email on file'}</strong>
            <small>{account?.role === 'admin' ? 'Admin account' : 'Collector account'} / {account?.verifiedBidder ? 'Verified bidder' : 'Bidder not yet verified'}</small>
          </article>
          <article className="obc-profile-panel__card">
            <span>Shipping status</span>
            <strong>{shippingReady ? 'Profile complete' : 'Needs attention'}</strong>
            <small>{shippingReady ? `${shipping.city}, ${shipping.state} ${shipping.postalCode || ''}` : 'Fill every shipping field before checkout.'}</small>
          </article>
          <article className="obc-profile-panel__card">
            <span>Notification coverage</span>
            <strong>{activePreferences} active alerts</strong>
            <small>Manage outbid, winning, ending, checkout, drop, and announcement alerts in Settings.</small>
          </article>
          <div className="obc-profile-panel__links">
            <button type="button" onClick={() => onSectionChange?.('alerts')}>View notifications</button>
            <button type="button" onClick={() => onSectionChange?.('payments')}>Open payments</button>
          </div>
        </aside>
      </div>
    </section>
  )
}

const notificationPriorityTypes = new Set(['outbid', 'checkout-reminder', 'auction-ending'])
const notificationSuccessTypes = new Set(['winner', 'winning'])
const notificationUpdateTypes = new Set(['new-drop', 'admin-announcement', 'announcement'])

function normalizeNotificationType(type) {
  return String(type || '').trim().toLowerCase().replace(/\s+/g, '-')
}

function getNotificationTone(type) {
  const normalized = normalizeNotificationType(type)
  if (notificationPriorityTypes.has(normalized)) return 'danger'
  if (notificationSuccessTypes.has(normalized)) return 'success'
  if (notificationUpdateTypes.has(normalized)) return 'info'
  return 'neutral'
}

function getNotificationLabel(type) {
  const normalized = normalizeNotificationType(type)
  return normalized
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function getNotificationIcon(type) {
  const normalized = normalizeNotificationType(type)
  if (normalized === 'outbid' || normalized === 'checkout-reminder') return <FaTriangleExclamation />
  if (notificationSuccessTypes.has(normalized)) return <FaCircleCheck />
  if (normalized === 'auction-ending') return <FaClock />
  if (notificationUpdateTypes.has(normalized)) return <FaEnvelope />
  return <FaBell />
}

function NotificationsPanel({ notices = [], expanded = false, onMarkAllRead, onMarkRead, onOpenNotice, onSectionChange }) {
  const orderedNotices = useMemo(() => [...notices].sort((a, b) => {
    if (Boolean(a.read) !== Boolean(b.read)) return a.read ? 1 : -1

    const aTime = new Date(a.createdAt || a.time || 0).getTime()
    const bTime = new Date(b.createdAt || b.time || 0).getTime()

    if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return bTime - aTime

    return String(a.id || a.time || '').localeCompare(String(b.id || b.time || ''))
  }), [notices])
  const unreadCount = orderedNotices.filter((notice) => !notice.read).length
  const priorityCount = orderedNotices.filter((notice) => notificationPriorityTypes.has(normalizeNotificationType(notice.type))).length
  const winCount = orderedNotices.filter((notice) => notificationSuccessTypes.has(normalizeNotificationType(notice.type))).length
  const updateCount = orderedNotices.filter((notice) => notificationUpdateTypes.has(normalizeNotificationType(notice.type))).length

  return (
    <section className={`tactical-card obc-dashboard__activity ${expanded ? 'is-expanded' : ''}`}>
      <div className="obc-dashboard__side-head">
        <div>
          <h3>Notifications</h3>
          <span>{notices.length} items</span>
        </div>
        <div className="obc-dashboard__section-actions">
          <button type="button" onClick={() => onSectionChange?.('alerts')}>View all</button>
          <button type="button" onClick={onMarkAllRead} disabled={!unreadCount}>Mark all read</button>
        </div>
      </div>
      <div className="obc-notifications__summary">
        <article><strong>{unreadCount}</strong><span>Unread</span></article>
        <article><strong>{priorityCount}</strong><span>Urgent</span></article>
        <article><strong>{winCount}</strong><span>Wins</span></article>
        <article><strong>{updateCount}</strong><span>Updates</span></article>
      </div>
      <div className="obc-dashboard__activity-list">
        {orderedNotices.map((notice) => {
          const type = normalizeNotificationType(notice.type)
          const tone = getNotificationTone(type)
          const label = getNotificationLabel(type)

          return (
            <article className={`obc-notice-card ${notice.read ? 'is-read' : 'is-unread'} is-${type}`} key={notice.id || `${type}-${notice.time}`}>
              <span className={`obc-notice-card__icon is-${tone}`}>{getNotificationIcon(type)}</span>
              <div className="obc-notice-card__content">
                <div className="obc-notice-card__heading">
                  <strong className="obc-notice-card__title">{label}</strong>
                  <span className={`obc-notice-card__badge is-${notice.read ? 'read' : 'unread'}`}>{notice.read ? 'Read' : 'Unread'}</span>
                </div>
                <p className="obc-notice-card__message">{notice.message}</p>
                <div className="obc-notice-card__meta">
                  <small>{notice.time}</small>
                  <small>{label}</small>
                </div>
              </div>
              <div className="obc-notice-card__actions">
                {notice.actionPath ? <button type="button" onClick={() => onOpenNotice?.(notice)}>Open</button> : null}
                {!notice.read ? <button type="button" className="is-ghost" onClick={() => onMarkRead?.(notice)}>Mark read</button> : null}
              </div>
            </article>
          )
        })}
      </div>
      {!orderedNotices.length ? <p className="obc-dashboard__empty-state">No notifications yet.</p> : null}
    </section>
  )
}

const defaultNotificationPreferences = {
  outbid: true,
  auctionEnding: true,
  winning: true,
  checkoutReminders: true,
  newDrops: true,
  adminAnnouncements: true,
}

const notificationPreferenceOptions = [
  { key: 'outbid', title: 'Outbid alerts', detail: 'Notify when another bidder takes the lead.' },
  { key: 'auctionEnding', title: 'Auction ending alerts', detail: 'Remind me when watched auctions are close to ending.' },
  { key: 'winning', title: 'Winning notices', detail: 'Send confirmations for won auctions and successful bids.' },
  { key: 'checkoutReminders', title: 'Checkout reminders', detail: 'Remind me about unpaid wins and weekly bundle checkout.' },
  { key: 'newDrops', title: 'New drops', detail: 'Notify me when OBC publishes new auctions or buy-now inventory.' },
  { key: 'adminAnnouncements', title: 'Admin announcements', detail: 'Receive account, policy, and marketplace announcements.' },
]

function SettingsPanel({ account, onUpdateNotificationPreferences, onUpdateProfile }) {
  const preferences = {
    ...defaultNotificationPreferences,
    ...(account?.notificationPreferences || {}),
  }
  const enabledCount = Object.values(preferences).filter(Boolean).length
  const shipping = account?.shippingProfile || {}

  const handleSubmit = (event) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const nextPreferences = Object.keys(defaultNotificationPreferences).reduce((next, key) => {
      next[key] = form.get(key) === 'on'
      return next
    }, {})

    onUpdateNotificationPreferences?.(nextPreferences)
  }

  const handleProfileSubmit = (event) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    onUpdateProfile?.({
      name: form.get('name') || account?.name || 'Collector',
      shippingProfile: {
        name: form.get('shippingName') || account?.name || 'Collector',
        line1: shipping.line1 || '',
        line2: shipping.line2 || '',
        city: shipping.city || '',
        state: shipping.state || '',
        postalCode: shipping.postalCode || '',
        country: shipping.country || 'US',
        phone: shipping.phone || '',
      },
    })
  }

  return (
    <section className="tactical-card obc-dashboard__detail-panel">
      <div className="obc-dashboard__side-head">
        <h3>Settings</h3>
      </div>
      <div className="obc-dashboard__detail-grid">
        <article><FaUser /><strong>{account?.name || 'Collector profile'}</strong><span>{account?.email || 'Display name, email, and bidder identity ready.'}</span></article>
        <article><FaEnvelope /><strong>{enabledCount} alert types active</strong><span>Choose which marketplace events should create account notifications.</span></article>
        <article><FaShieldHalved /><strong>Security</strong><span>Verified bidder controls and saved card checks enabled.</span></article>
      </div>
      <div className="obc-dashboard__settings-columns">
        <form className="obc-dashboard__settings-form" onSubmit={handleProfileSubmit}>
          <div className="obc-dashboard__settings-form-head">
            <h4>Account info</h4>
            <p>Update the user identity that appears in the dashboard header and account cards.</p>
          </div>
          <label>
            Display name
            <input name="name" defaultValue={account?.name || ''} placeholder="Collector name" required />
          </label>
          <label>
            Shipping name
            <input name="shippingName" defaultValue={shipping.name || account?.name || ''} placeholder="Full legal name" required />
          </label>
          <label>
            Email
            <input value={account?.email || ''} readOnly />
          </label>
          <button className="obc-dashboard__bid-button" type="submit">Save Account Info</button>
        </form>

        <form className="obc-preferences-form" onSubmit={handleSubmit}>
          {notificationPreferenceOptions.map((option) => (
            <label className="obc-preference-toggle" key={option.key}>
              <input name={option.key} type="checkbox" defaultChecked={preferences[option.key]} />
              <span>
                <strong>{option.title}</strong>
                <small>{option.detail}</small>
              </span>
            </label>
          ))}
          <button className="obc-dashboard__bid-button" type="submit">Save Notification Preferences</button>
        </form>
      </div>
    </section>
  )
}

export default UserDashboard
